import * as React from 'react'
import {useState} from 'react'
import {Lang} from 'yougile-lang'
import {ObjUtil, Util} from 'yougile-util'
import {DataObjectId, Hub, PermissionCustomRole} from 'yougile-model'
import {Sync} from '@client/sync'
import {useGlobalKeyDown} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {Board} from '@extensions/board'
import {globals} from '@extensions/globals-base'
import {Project} from '@extensions/project'

interface Props {
  onChoose(chosenColumns: Hub[], role?: PermissionCustomRole): void
  chooseBoards?: boolean
  singleColumn?: boolean
  chooseProjects?: boolean
  chooseRoles?: boolean
  filter?: (hub: Hub) => boolean
  className?: string
}

interface State {
  currentHub?: Hub
  chosenColumns: Hub[]
  items?: Hub[] // list of items in current hub
  level?: number // see titles const for explanation
}

// eslint-disable-next-line max-lines-per-function
export const ColumnChooser = (props: Props) => {
  const box = Sync.getCurrentSandbox()
  const [state, setState] = useState<State>(() => ({
    currentHub: box.get(box.companyId) as Hub,
    chosenColumns: [],
    items: [],
    level: 0
  }))

  const listItemsIn = (level: number, hub: Hub): Hub[] => {
    const box = Sync.get(hub)
    let res: Hub[]
    if (level === 1) {
      // projects
      res = Project.listProjects(box.companyId, 'not-archive', true).sort(
        Project.projectOrderComparer
      )
    } else if (level === 3) {
      // columns
      res = Board.listColumns(hub, {sorted: true, noMirrors: true})
    } else {
      // boards
      res = Project.listBoards(hub, true)
    }
    return props.filter ? res.filter(props.filter) : res
  }

  const goTo = (hub: Hub): void => {
    if (state.level >= 3 || !state.items.includes(hub)) {
      throw new Error('Operation disallowed')
    }
    setState({
      ...state,
      level: state.level + 1,
      items: listItemsIn(state.level + 1, hub),
      currentHub: hub
    })
  }

  const goUp = (): void => {
    if (
      state.level <= 0 ||
      !state.currentHub ||
      !(state.currentHub instanceof Hub)
    ) {
      throw new Error('Operation disallowed')
    }
    const newCurr = Sync.get(state.currentHub).get(
      state.currentHub.location
    ) as Hub
    setState({
      ...state,
      level: state.level - 1,
      items: listItemsIn(state.level - 1, newCurr),
      currentHub: newCurr
    })
  }

  const cleanState = (): void => {
    if (!state.currentHub) {
      return
    }
    const projects: Hub[] = []
    const box = Sync.get(state.currentHub)
    box
      .forwardConnections(globals.currentUserId)
      .forEach((objId: DataObjectId) => {
        const e = box.get(objId)
        if (
          e instanceof Hub &&
          !e.deleted &&
          !e.title.startsWith('.') &&
          !box.isDeleted(objId)
        ) {
          projects.push(e)
        }
      })
    if (projects.length === 1) {
      setState({
        ...state,
        currentHub: undefined,
        chosenColumns: [],
        items: listItemsIn(1, projects[0]),
        level: 1
      })
    } else if (projects.length > 1) {
      setState({
        ...state,
        currentHub: undefined,
        chosenColumns: [],
        items: projects,
        level: 0
      })
    }
  }

  const toggleChosen = (col: Hub): void => {
    const chosen = state.chosenColumns
    const ix = chosen.indexOf(col)
    if (ix === -1) {
      chosen.push(col)
    } else {
      chosen.splice(ix, 1)
    }
    setState({...state, chosenColumns: chosen})
  }

  React.useEffect(() => {
    cleanState()
  }, [])

  const {chosenColumns, level, items, currentHub} = state
  const {className, chooseBoards, chooseProjects, chooseRoles, onChoose} = props

  const up = () => goUp()

  const count = chosenColumns.length ? (
    <div className="column-chooser-count">
      {Lang.msg.chosenColumns}: {chosenColumns.length}
    </div>
  ) : undefined
  const stopLevel = chooseProjects ? 1 : chooseBoards ? 2 : 3

  const goUpComponent =
    level > 1 ? (
      <div className="column-chooser-up _link" onClick={up}>
        <i className="fa fa-chevron-left" /> {Lang.msg.back}
      </div>
    ) : undefined

  const bem = Util.bemFrom('column-chooser-item')
  const roles = ObjUtil.values(currentHub?.data?.roles)
  const entries =
    chooseRoles && level === 2 ? (
      roles.length === 0 ? (
        <div className={bem('empty')}>{Lang.msg.proj.noCustomRoles}</div>
      ) : (
        roles.map(r => {
          const click = () => onChoose([currentHub], r)
          return (
            <div className={bem()} key={r.key} onClick={click}>
              {r.name}
            </div>
          )
        })
      )
    ) : (
      items.map(hub => {
        const toggle = () => toggleChosen(hub)
        const onClickGoTo = () => {
          if (level === stopLevel && props.singleColumn) {
            onChoose([hub])
          } else {
            goTo(hub)
          }
        }
        const withCheckbox = level === stopLevel && !props.singleColumn
        return (
          <div key={hub.id}>
            {withCheckbox && (
              <div className={bem({option: true})}>
                <label>
                  <input
                    type="checkbox"
                    className="_checkbox"
                    checked={chosenColumns.includes(hub)}
                    onChange={toggle}
                  />
                  <span className="_checkbox" />
                  <span className={bem('title')}>{hub.title}</span>
                </label>
              </div>
            )}
            {!withCheckbox && (
              <div className="column-chooser-item" onClick={onClickGoTo}>
                {hub.title}
              </div>
            )}
          </div>
        )
      })
    )

  const ok = () => {
    if (chosenColumns.length) {
      onChoose(chosenColumns)
      requestAnimationFrame(() => cleanState())
    }
  }
  const cancel = () => {
    onChoose([])
    requestAnimationFrame(() => cleanState())
    return true
  }
  const columnClassName =
    'column-chooser-submit _button' + (!chosenColumns.length ? ' disabled' : '')

  const titles = [
    Lang.msg.companiesList,
    Lang.msg.projects,
    chooseRoles ? Lang.msg.proj.roles : Lang.msg.boards,
    Lang.msg.columns
  ]

  useGlobalKeyDown({escape: cancel})

  return (
    <div className={cn('column-chooser', className)}>
      <div className="column-chooser-title">
        <div className="column-chooser__title-text">{titles[level]}:</div>
        {goUpComponent}
      </div>
      <div className="column-chooser-item-list">{entries}</div>
      {!chooseRoles && (
        <div className="column-chooser-actions">
          {count}
          {!props.singleColumn && (
            <div className={columnClassName} onClick={ok}>
              {Lang.msg.add}
            </div>
          )}
          <div className="column-chooser-cancel _link" onClick={cancel}>
            {Lang.msg.cancel}
          </div>
        </div>
      )}
    </div>
  )
}
